// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  UserCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { UserService, User as UserProfile } from '../admin/pages/users/userService';

// ── Extended profile that carries the resolved permissions array ──────────────
export interface UserProfileWithPermissions extends UserProfile {
  permissions: string[]; // feature codes from the assigned role document
}

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfileWithPermissions | null;
  login: (email: string, password: string) => Promise<UserCredential>;
  signup: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  loading: boolean;
  profileLoading: boolean;
  refreshUserProfile: () => Promise<void>;
  /** Convenience: returns true if the current user has the given feature code */
  hasPermission: (code: string) => boolean;
  /** True when role === 'super-admin' — bypasses all permission checks */
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// ── Sentinel: role string stored in userClaims.role for super-admin ───────────
export const SUPER_ADMIN_ROLE = 'super-admin';

// ── Helper: load permissions from the roles collection ────────────────────────
// For super-admin we return the sentinel array so every permission check passes.
// For all other users we fetch the role document and return its permissions array.
async function loadRolePermissions(
  roleId: string | null | undefined,
  legacyRole?: string
): Promise<string[]> {
  if (legacyRole === SUPER_ADMIN_ROLE) return [SUPER_ADMIN_ROLE];
  if (!roleId) return [];
  try {
    const roleSnap = await getDoc(doc(db, 'roles', roleId));
    if (!roleSnap.exists()) return [];
    const data = roleSnap.data();
    return Array.isArray(data.permissions) ? (data.permissions as string[]) : [];
  } catch (err) {
    console.error('Error loading role permissions:', err);
    return [];
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfileWithPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  // ── Core fetch: get userClaims doc then resolve role permissions ──────────
  const fetchUserProfile = async (uid: string) => {
    try {
      setProfileLoading(true);
      const profile = await UserService.getUserClaims(uid);
      if (!profile) {
        setUserProfile(null);
        return;
      }

      // super-admin gets the sentinel — no extra Firestore read needed.
      const permissions = await loadRolePermissions(profile.roleId, profile.role);

      setUserProfile({ ...profile, permissions });
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const refreshUserProfile = async () => {
    if (currentUser?.uid) {
      await fetchUserProfile(currentUser.uid);
    }
  };

  // ── signup ────────────────────────────────────────────────────────────────
  const signup = async (email: string, password: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    try {
      await UserService.createUserClaims(
        userCredential.user.uid,
        userCredential.user.email || email,
        userCredential.user.displayName || undefined,
        userCredential.user.photoURL || undefined
      );
      await fetchUserProfile(userCredential.user.uid);
    } catch (error) {
      console.error('Error creating user claims:', error);
    }
    return userCredential;
  };

  // ── login — blocks inactive users ─────────────────────────────────────────
  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    if (userCredential.user?.uid) {
      const profile = await UserService.getUserClaims(userCredential.user.uid);

      if (!profile?.isActive) {
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact the administrator.');
      }

      // Resolve permissions immediately after login
      const permissions = await loadRolePermissions(profile.roleId, profile.role);
      setUserProfile({ ...profile, permissions });
    }

    return userCredential;
  };

  // ── logout ────────────────────────────────────────────────────────────────
  const logout = async () => {
    await signOut(auth);
    setUserProfile(null);
  };

  // ── onAuthStateChanged ────────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user?.uid) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
        setProfileLoading(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // ── Convenience helpers ───────────────────────────────────────────────────
  // isSuperAdmin is true when the stored role string equals 'super-admin'.
  // This is the single source of truth — every other check reads this flag.
  const isSuperAdmin = userProfile?.role === SUPER_ADMIN_ROLE;

  const hasPermission = (code: string): boolean => {
    if (!userProfile) return false;
    // super-admin bypasses every individual permission check
    if (isSuperAdmin) return true;
    return userProfile.permissions.includes(code);
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    login,
    signup,
    logout,
    loading,
    profileLoading,
    refreshUserProfile,
    hasPermission,
    isSuperAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};


// import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
// import { 
//   User,
//   UserCredential,
//   signInWithEmailAndPassword, 
//   createUserWithEmailAndPassword, 
//   signOut, 
//   onAuthStateChanged 
// } from 'firebase/auth';
// import { auth } from '../firebase/config';
// import { UserService, User as UserProfile } from '../admin/pages/users/userService';

// interface AuthContextType {
//   currentUser: User | null;
//   userProfile: UserProfile | null;
//   login: (email: string, password: string) => Promise<UserCredential>;
//   signup: (email: string, password: string) => Promise<UserCredential>;
//   logout: () => Promise<void>;
//   loading: boolean;
//   profileLoading: boolean;
//   refreshUserProfile: () => Promise<void>;
// }

// const AuthContext = createContext<AuthContextType | undefined>(undefined);

// export const useAuth = () => {
//   const context = useContext(AuthContext);
//   if (context === undefined) {
//     throw new Error('useAuth must be used within an AuthProvider');
//   }
//   return context;
// };

// interface AuthProviderProps {
//   children: ReactNode;
// }

// export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
//   const [currentUser, setCurrentUser] = useState<User | null>(null);
//   const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
//   const [loading, setLoading] = useState(true);
//   const [profileLoading, setProfileLoading] = useState(true);

//   // Function to fetch user profile claims
//   const fetchUserProfile = async (uid: string) => {
//     try {
//       setProfileLoading(true);
//       const profile = await UserService.getUserClaims(uid);
//       setUserProfile(profile);
//     } catch (error) {
//       console.error('Error fetching user profile:', error);
//       setUserProfile(null);
//     } finally {
//       setProfileLoading(false);
//     }
//   };

//   // Function to refresh user profile (can be called from anywhere)
//   const refreshUserProfile = async () => {
//     if (currentUser?.uid) {
//       await fetchUserProfile(currentUser.uid);
//     }
//   };

//   const signup = async (email: string, password: string) => {
//     const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
//     // Create user claims after signup
//     try {
//       await UserService.createUserClaims(
//         userCredential.user.uid,
//         userCredential.user.email || email,
//         userCredential.user.displayName || undefined,
//         userCredential.user.photoURL || undefined
//       );
      
//       // Fetch the newly created profile
//       await fetchUserProfile(userCredential.user.uid);
//     } catch (error) {
//       console.error('Error creating user claims:', error);
//     }
    
//     return userCredential;
//   };

//   // const login = async (email: string, password: string) => {
//   //   const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
//   //   // Fetch user profile after login
//   //   if (userCredential.user?.uid) {
//   //     await fetchUserProfile(userCredential.user.uid);
//   //   }
    
//   //   return userCredential;
//   // };

//   const login = async (email: string, password: string) => {
//   const userCredential = await signInWithEmailAndPassword(auth, email, password);

//   // Fetch user profile and check isActive before allowing login
//   if (userCredential.user?.uid) {
//     const profile = await UserService.getUserClaims(userCredential.user.uid);

//     if (!profile?.isActive) {
//       // Sign out immediately so the Firebase session is not kept
//       await signOut(auth);
//       throw new Error('Your account has been deactivated. Please contact the administrator.');
//     }

//     setUserProfile(profile);
//   }

//   return userCredential;
// };

//   const logout = async () => {
//     await signOut(auth);
//     setUserProfile(null);
//   };

//   useEffect(() => {
//     const unsubscribe = onAuthStateChanged(auth, async (user) => {
//       setCurrentUser(user);
      
//       if (user?.uid) {
//         // Fetch user profile when auth state changes
//         await fetchUserProfile(user.uid);
//       } else {
//         setUserProfile(null);
//         setProfileLoading(false);
//       }
      
//       setLoading(false);
//     });

//     return unsubscribe;
//   }, []);

//   const value: AuthContextType = {
//     currentUser,
//     userProfile,
//     login,
//     signup,
//     logout,
//     loading,
//     profileLoading,
//     refreshUserProfile
//   };

//   return (
//     <AuthContext.Provider value={value}>
//       {!loading && children}
//     </AuthContext.Provider>
//   );
// };